import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { getDefaultTaskStatusTab } from '../../lib/task-list';
import { trpc } from '../../lib/trpc';
import {
  emptyTaskForm,
  type TaskFormValues,
  taskFormSchema,
  toCreateTaskInput,
} from '../../types/task-form';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { TaskFormFields } from './TaskFormFields';

/** The payload the dialog emits — already shaped for `tasks.create`. */
export type CreateTaskInput = ReturnType<typeof toCreateTaskInput>;

export interface CreateTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Receives the payload already shaped for `tasks.create` — the date/time folding and reminder
   * offset are done once, in `toCreateTaskInput`, rather than by each caller.
   */
  onSubmit: (data: CreateTaskInput) => void;
  initialTitle: string;
  initialDescription: string;
  workspaceMembers: Array<{ id: number; email: string; fullName: string | null }>;
  isSubmitting?: boolean;
  /** When true, a due date must be picked before the task can be submitted (e.g. the Calendar). */
  requireDueDate?: boolean;
  /** Pre-selects a related contact (e.g. the contact already linked to a thread). */
  initialContactId?: number | null;
  /** Pre-links the page a task belongs to. */
  initialAnchorId?: number | null;
  /** Brands an invite created from this point-of-need assignee control. */
  invitePortal?: 'clinic' | 'skill-viper';
}

/**
 * Create a task from a portal.
 *
 * The fields themselves are `TaskFormFields`, shared with the extension's create/edit route, so
 * a task carries the same capabilities wherever it was authored. This dialog owns only its
 * chrome, its submit, and the invite-by-email affordance beside the assignee picker.
 */
export function CreateTaskDialog({
  isOpen,
  onClose,
  onSubmit,
  initialTitle,
  initialDescription,
  workspaceMembers,
  isSubmitting = false,
  requireDueDate = false,
  initialContactId = null,
  initialAnchorId = null,
  invitePortal = 'clinic',
}: CreateTaskDialogProps) {
  const meQuery = trpc.auth.me.useQuery();
  const currentUserId = meQuery.data?.uid ?? null;
  const statusesQuery = trpc.taskStatuses.list.useQuery();
  const statuses = statusesQuery.data?.items ?? [];
  const defaultStatus = getDefaultTaskStatusTab(statuses);

  const [inviteEmail, setInviteEmail] = useState('');
  const utils = trpc.useUtils();

  const inviteMemberMutation = trpc.workspace.addMember.useMutation({
    onSuccess: (result) => {
      setInviteEmail('');
      utils.workspace.listMembers.invalidate();
      if (result.directAdded) {
        toast.success('Teammate added. Choose them as the assignee when the list refreshes.');
      } else {
        toast.success('Invitation sent. You can finish assigning this task once they join.');
      }
    },
    onError: (error) => toast.error(error.message || 'Could not invite teammate.'),
  });

  const methods = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    // Submit is gated on validity, which react-hook-form only keeps current under a validating
    // mode — the same trap the extension's form hit.
    mode: 'onChange',
    defaultValues: {
      ...emptyTaskForm(defaultStatus),
      title: initialTitle,
      description: initialDescription,
      contactId: initialContactId,
      anchorId: initialAnchorId,
      assignedTo: currentUserId,
      dueEnabled: requireDueDate,
    },
  });

  // Re-seed when the dialog reopens against different context, and once the workspace's default
  // status and current user resolve (both arrive after the first render).
  useEffect(() => {
    if (!isOpen) return;
    methods.reset({
      ...emptyTaskForm(defaultStatus),
      title: initialTitle,
      description: initialDescription,
      contactId: initialContactId,
      anchorId: initialAnchorId,
      assignedTo: currentUserId,
      dueEnabled: requireDueDate,
    });
    void methods.trigger();
  }, [
    isOpen,
    initialTitle,
    initialDescription,
    initialContactId,
    initialAnchorId,
    currentUserId,
    defaultStatus,
    requireDueDate,
    methods,
  ]);

  const handleSubmit = methods.handleSubmit((values) => {
    if (requireDueDate && !values.dueDate) {
      methods.setError('dueDate', { message: 'A due date is required' });
      return;
    }
    onSubmit(toCreateTaskInput(values));
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[540px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Task</DialogTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Detail the task parameters before committing action steps.
          </p>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TaskFormFields
              statuses={statuses}
              members={workspaceMembers}
              requireDueDate={requireDueDate}
              disabled={isSubmitting}
              footerSlot={
                <div className="space-y-1.5">
                  <Label htmlFor="invite-email" className="text-xs font-semibold">
                    Invite a teammate
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="invite-email"
                      aria-label="Invite teammate by email"
                      type="email"
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      placeholder="Invite teammate by email"
                      className="h-8 text-xs"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!inviteEmail.trim() || inviteMemberMutation.isPending}
                      onClick={() =>
                        inviteMemberMutation.mutate({
                          email: inviteEmail.trim(),
                          role: 'editor',
                          portal: invitePortal,
                        })
                      }
                    >
                      {inviteMemberMutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <UserPlus className="h-3.5 w-3.5" />
                      )}
                      <span className="sr-only">Invite teammate</span>
                    </Button>
                  </div>
                </div>
              }
            />

            <DialogFooter className="flex justify-end gap-2 border-t border-border/60 pt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Task'}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
